import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { AuthContext } from '../context/AuthContext';
import { useModalA11y } from '../hooks/useModalA11y';

// Cmd+K command palette. Press ⌘K (mac) or Ctrl+K (everywhere else)
// from anywhere in the app to open a fuzzy-search modal of every
// nav route. Filtered live as you type; ↑/↓ to move, Enter to go,
// Esc to close.
//
// Routes are picked per user role so an organiser doesn't see
// "/dashboard" (artist) and vice versa. Logged-out users see only
// the public surfaces — same content as the marketing header.

const ARTIST_COMMANDS = [
  { label: 'Dashboard',         to: '/dashboard',         keywords: 'home stats overview' },
  { label: 'Bookings',          to: '/artist-bookings',   keywords: 'requests gigs' },
  { label: 'Messages',          to: '/inquiries',         keywords: 'inbox dms inquiries' },
  { label: 'Profile',           to: '/profile',           keywords: 'edit bio mixes' },
  { label: 'Tech rider',        to: '/profile?tab=rider', keywords: 'rider gear pioneer cdj' },
  { label: 'Press kit',         to: '/profile?tab=press-kit', keywords: 'pdf epk' },
  { label: 'Availability',      to: '/profile?tab=availability', keywords: 'calendar block dates' },
  { label: 'Brand',             to: '/profile?tab=brand', keywords: 'logo visuals' },
  { label: 'Set archive',       to: '/profile#mixes', keywords: 'mixes soundcloud' },
  { label: 'Analytics',         to: '/artist-analytics',  keywords: 'stats discoverability funnel followers' },
  { label: 'Earnings',          to: '/artist-earnings',   keywords: 'money paid pending csv tax' },
  { label: 'Community',         to: '/community',         keywords: 'forum posts' },
  { label: 'Activity feed',     to: '/feed',              keywords: 'updates news' },
  { label: 'Fill-in board',     to: '/fill-in',           keywords: 'cover spots last minute' },
  { label: 'Crews',             to: '/crews',             keywords: 'collectives groups' },
  { label: 'Dublin scene',      to: '/dublin',            keywords: 'discover venues activity' },
  { label: 'Settings',          to: '/artist-settings',   keywords: 'account password email tier' },
];

const ORGANIZER_COMMANDS = [
  { label: 'Bookings',          to: '/organizer-bookings', keywords: 'gigs requests sent' },
  { label: 'Find artists',      to: '/find-artists',       keywords: 'match search book djs' },
  { label: 'Browse directory', to: '/search',            keywords: 'all djs talent browse' },
  { label: 'CRM',               to: '/crm',                keywords: 'saved artists shortlist contacts' },
  { label: 'Messages',          to: '/inquiries',          keywords: 'inbox dms' },
  { label: 'Crews',             to: '/crews',              keywords: 'collectives' },
  { label: 'Fill-in board',     to: '/fill-in',            keywords: 'cover gigs' },
  { label: 'Activity feed',     to: '/feed',               keywords: 'news updates' },
  { label: 'Payments',          to: '/payments',           keywords: 'invoices money owed' },
  { label: 'Analytics',         to: '/analytics',          keywords: 'stats' },
  { label: 'Team',              to: '/team',               keywords: 'members access' },
  { label: 'Dublin scene',      to: '/dublin',             keywords: 'discover venues' },
  { label: 'Settings',          to: '/organizer-settings', keywords: 'account password email tier' },
];

// Logged-out users only see surfaces they can actually reach without
// signing in. /dublin, /search, /crews are all behind ProtectedRoute
// after the dual-platform separation pass — a Cmd+K command that
// silently bounces through /login is worse than no command at all.
const PUBLIC_COMMANDS = [
  { label: 'Sign in',        to: '/login',    keywords: 'login' },
  { label: 'Join the scene', to: '/register', keywords: 'sign up register' },
  { label: 'Home',           to: '/',         keywords: 'marketing landing' },
  { label: 'Terms',          to: '/terms',    keywords: 'legal' },
  { label: 'Privacy',        to: '/privacy',  keywords: 'legal' },
];

export const CommandPalette = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Build the command list once per role change. AuthContext flipping
  // (login/logout) is the only path that changes commands.
  const commands = useMemo(() => {
    if (!user) return PUBLIC_COMMANDS;
    return user.userType === 'artist' ? ARTIST_COMMANDS : ORGANIZER_COMMANDS;
  }, [user]);

  // Cheap fuzzy: lowercase substring across label + keywords. No
  // ranking refinement — list is short enough that "starts-with" vs
  // "contains" doesn't really change the perceived UX.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(c =>
      c.label.toLowerCase().includes(q) ||
      (c.keywords || '').toLowerCase().includes(q)
    );
  }, [commands, query]);

  // Reset selection on filter change so the highlighted row stays in
  // bounds when results shrink.
  useEffect(() => { setActiveIdx(0); }, [query, open]);

  // Global ⌘K / Ctrl+K to open. Capture mid-typing too — Cmd+K
  // shouldn't conflict with the browser address bar (Ctrl+L on most).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Reset query state when palette opens; useModalA11y handles focus
  // (data-autofocus on the input) and body-scroll lock.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
    }
  }, [open]);

  const dialogRef = useModalA11y(() => setOpen(false), open);

  if (!open) return null;

  const go = (cmd) => {
    setOpen(false);
    navigate(cmd.to);
  };

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filtered[activeIdx];
      if (cmd) go(cmd);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-[70] flex items-start justify-center pt-[5dvh] px-4"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-in"
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        className="relative w-full max-w-xl bg-night border border-line rounded-2xl shadow-2xl overflow-hidden animate-slide-down"
        onKeyDown={handleKey}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-line">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-fg-subtle shrink-0">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to anything…"
            enterKeyHint="go"
            data-autofocus
            className="flex-1 bg-transparent text-fg placeholder-fg-subtle text-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-acid rounded"
            aria-label="Search commands"
          />
          <kbd className="hidden sm:inline-block px-1.5 py-0.5 bg-night-100 text-fg-subtle text-[10px] font-mono rounded border border-line">esc</kbd>
        </div>
        <ul ref={listRef} className="max-h-[60dvh] overflow-y-auto py-1" style={{ overscrollBehavior: 'contain' }}>
          {filtered.length === 0 ? (
            <li className="px-4 py-3 text-sm text-fg-subtle">No matches.</li>
          ) : (
            filtered.map((cmd, i) => (
              <li key={cmd.to}>
                <button
                  type="button"
                  onClick={() => go(cmd)}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={`w-full text-left px-4 py-2.5 flex items-center justify-between text-sm transition ${
                    i === activeIdx ? 'bg-acid/10 text-acid' : 'text-fg hover:bg-glass'
                  }`}
                >
                  <span>{cmd.label}</span>
                  <span className="text-xs text-fg-subtle font-mono">{cmd.to}</span>
                </button>
              </li>
            ))
          )}
        </ul>
        <div className="px-4 py-2 border-t border-line flex items-center gap-3 text-[11px] text-fg-subtle">
          <span><kbd className="px-1 py-0.5 bg-night-100 rounded border border-line font-mono">↑↓</kbd> navigate</span>
          <span><kbd className="px-1 py-0.5 bg-night-100 rounded border border-line font-mono">⏎</kbd> open</span>
          <span><kbd className="px-1 py-0.5 bg-night-100 rounded border border-line font-mono">⌘K</kbd> toggle</span>
        </div>
      </div>
    </div>,
    document.body
  );
};
